
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { StoryCard } from '@/components/stories/StoryCard';
import { StorySubmissionForm } from '@/components/stories/StorySubmissionForm';
import { testimonials, getCategoryTestimonials, getCategoryInfo, Testimonial } from '@/data/stories/testimonials';
import { UserIcon, UsersIcon, HeartIcon, AcademicCapIcon, PlusIcon } from '@heroicons/react/24/outline';
import SEO from '@/components/SEO';
import { ImageOptimizer } from '@/components/seo/ImageOptimizer';

const Stories = () => {
  const [selectedCategory, setSelectedCategory] = useState<Testimonial['category'] | 'all'>('all');
  const [isSubmissionOpen, setIsSubmissionOpen] = useState(false);

  const categories = [
    { key: 'all' as const, title: 'All Stories', icon: UsersIcon, count: testimonials.length },
    { key: 'older_adult' as const, title: 'Older Adults', icon: UserIcon, count: getCategoryTestimonials('older_adult').length },
    { key: 'family_member' as const, title: 'Family Members', icon: HeartIcon, count: getCategoryTestimonials('family_member').length },
    { key: 'caregiver' as const, title: 'Caregivers', icon: HeartIcon, count: getCategoryTestimonials('caregiver').length },
    { key: 'professional' as const, title: 'Professionals', icon: AcademicCapIcon, count: getCategoryTestimonials('professional').length },
  ];

  const filteredTestimonials = selectedCategory === 'all' 
    ? testimonials 
    : getCategoryTestimonials(selectedCategory);

  const handleSubmissionSuccess = () => {
    setIsSubmissionOpen(false);
  };

  return (
    <>
      <SEO 
        title="Stories - SeniorThrive"
        description="Read inspiring stories from older adults, family members, caregivers, and healthcare professionals about thriving at home, care coordination, and aging with confidence."
        keywords="senior stories, caregiving testimonials, aging at home stories, family caregivers, healthcare professionals"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        {/* Hero Section with Background Image */}
        <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 flex items-center overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <ImageOptimizer 
              src="/lovable-uploads/284d6520-d388-4987-96db-cb389426ea19.png" 
              alt="Happy senior woman standing in her kitchen, embodying the spirit of thriving and independence at home"
              className=""
              imgClassName="w-full h-full object-cover" 
              sizes="100vw"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background"></div>
          </div>

          <div className="relative max-w-7xl mx-auto text-center z-10">
            
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
              Stories of <span className="text-primary">Thriving</span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Real experiences from our community of older adults, families, caregivers, and professionals 
              who are transforming the way we age and care for one another.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Dialog open={isSubmissionOpen} onOpenChange={setIsSubmissionOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" className="inline-flex items-center gap-2">
                    <PlusIcon className="w-5 h-5" />
                    Share Your Story
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <StorySubmissionForm onSuccess={handleSubmissionSuccess} />
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="px-4 sm:px-6 lg:px-8 pb-12 pt-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map((category) => {
                const Icon = category.icon;
                const isActive = selectedCategory === category.key;
                
                return (
                  <Button
                    key={category.key}
                    variant={isActive ? "default" : "outline"}
                    onClick={() => setSelectedCategory(category.key)}
                    className="inline-flex items-center gap-2"
                  >
                    <Icon className="w-4 h-4" />
                    {category.title}
                    <span className="bg-background/20 text-xs px-2 py-1 rounded-full">
                      {category.count}
                    </span>
                  </Button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stories Grid */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20">
          <div className="max-w-7xl mx-auto">
            {selectedCategory !== 'all' && (
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  {getCategoryInfo(selectedCategory).title}
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  {getCategoryInfo(selectedCategory).subtitle}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTestimonials.map((testimonial) => (
                <StoryCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>

            {filteredTestimonials.length === 0 && (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-6">
                  <UsersIcon className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  No stories found
                </h3>
                <p className="text-muted-foreground">
                  Be the first to share a story in this category!
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Call to Action */}
        <section className="bg-primary/5 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Have a Story to Share?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Your experience could inspire and help others in our community. Share your journey with aging, 
              caregiving, or supporting loved ones.
            </p>
            
            <Dialog open={isSubmissionOpen} onOpenChange={setIsSubmissionOpen}>
              <DialogTrigger asChild>
                <Button size="lg" className="inline-flex items-center gap-2">
                  <PlusIcon className="w-5 h-5" />
                  Submit Your Story
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <StorySubmissionForm onSuccess={handleSubmissionSuccess} />
              </DialogContent>
            </Dialog>
          </div>
        </section>
      </div>
    </>
  );
};

export default Stories;
